// Configurator business rules.
//
// HubSpot is the source of truth for product NAMES + PRICES + SKU IDS, but
// HubSpot has nothing to say about which products fit which machines, what
// blocks what, or what comes pre-selected. All of that lives here.
//
// Rules are keyed by HubSpot product ID (string). When a product is added,
// removed, or renamed in HubSpot, only the ID's row here needs updating.
//
// Categories of rule we capture:
//   • displayName / description overrides         — customer-facing text
//   • hide                                        — don't show in configurator
//   • compat                                      — when the product is selectable
//   • includedWhen                                — when it shows as "Included" (locked, $0)
//   • exclusiveTo                                 — only visible when a specific upgrade is selected
//   • defaultOnMachine / defaultOnUpgrade         — auto-add when that selection is made
//   • blocks                                      — auto-remove these other product IDs when this is added
//   • suggestsOnRemoval                           — when this is removed, suggest re-adding these
//   • warnings                                    — soft messages tied to context
//   • priceOverride                               — dynamic price (e.g. Accelerate's −$500 strikethrough)

import type { Catalog, MachineFamily } from "./catalog";

/* ── Product ID constants ─────────────────────────────────────────
   Centralized so a HubSpot ID change is a one-line update. */

// Machines
export const ID = {
  // Machines (intentionally not listed here — machines have no rules of
  // their own beyond what's in catalog.ts; everything else REFERENCES them
  // by family/size, not by ID).

  // Upgrades
  ASCEND_RETROFIT: "1331273253",
  STATLER_RETRO: "1331265923", // displays as "Statler Retrofit"
  VISION_2_RETROFIT: "1331267173",
  ASCEND_UPGRADE: "1331269695",

  // Tables — HP2
  HP2_8: "1331272234",
  HP2_10: "1331263393", // $0 included option for 18"
  HP2_11: "1331266615",
  HP2_12: "1331270731", // $0 included option for 18" (default)
  // Tables — GS6
  GS6_8: "1331265921",
  GS6_10: "1331270507",
  GS6_11: "1331270508",
  GS6_12: "1331264351", // $0 included option for 22/26/30" (default)
  GS6_14: "1331269694",

  // Accessories (16 original)
  HYDRAULICS: "1331263395",
  // Two HubSpot products are named "Casters"-ish:
  //   • 1331269696  name="Casters"  sku="Casters"           ← canonical
  //   • 1230166682  name="Swivel Caster Set..."  sku="500-0036"  ← legacy
  // Andrew flagged the canonical one in HubSpot UI and un-flagged the
  // legacy one. Reflecting that here so the rule keys keep their intent
  // names (CASTERS = the one you actually use).
  CASTERS: "1331269696",
  CASTERS_OLD: "1230166682", // hidden (no longer eligible in HubSpot anyway)
  LIGHT_BAR: "1331270732",
  QUILT_CLIPS: "1331270733",
  RULER_BASE: "1331264352",
  MICRO_FILL: "1331272580",
  VIVID_LIGHT: "1331278404",
  CUTTING_MAT: "2128594013",
  BOBBIN_WINDER: "1230118913",
  STARTUP_KIT: "1334023757",
  PANTO_HANDLES: "1331270510",
  APPROACH_FOOT_NEW_MACHINE: "16179159167", // $389 w/ Machine Purchase
  APPROACH_FEET_RETROFIT: "1331273251", // $549 for retrofits
  ZIPPERED_LEADERS_NEW_MACHINE: "1331268375", // $329
  ZIPPERED_LEADERS_RETROFIT: "2243344204", // $379
  MEMBERSHIP: "2384325499",
  OLD_APPROACH_FOOT_SYSTEM: "1313845782", // hidden (replaced by the two priced variants)

  // Ascend-Upgrade-exclusive accessories
  CPU_MOUNT_ONLY: "1373526290", // $263.35
  CPU_AND_MONITOR_MOUNT: "2182037789", // $516.35 — the "$500-ish" force-add for Boersma
  MONITOR_MOUNT_ONLY: "2135425140", // $235
  DELL_MONITOR: "2395415106",
  GAMMILL_COMPUTER_XE5: "38073547862", // force-add when Win 7 or older
  STATLER_KEYBOARD: "1229702817",
  STATLER_MOUSE: "2137036846",

  // Vision-2-Retrofit-exclusive accessories
  OS_WHEEL_KIT: "2135274109",
} as const;

/* ── Types ─────────────────────────────────────────────────────── */

export type PurchaseContext = "new_machine" | "upgrade";

export interface CompatRule {
  // OR-ed semantics: a product is "compatible" if EITHER its
  // machineFamilies match the new-machine context, OR its upgradeIds match
  // the upgrade context. Universal accessories leave both unset.
  machineFamilies?: MachineFamily[];
  upgradeIds?: string[];
  throatSizes?: number[];
  // A coarse gate that's applied first — limits the product to
  // new-machine OR upgrade purchase context broadly.
  purchaseContexts?: PurchaseContext[];
  // Per-answer hide rule. When the customer's answer to an
  // existing-system field matches any value in `equalsAnyOf`, this
  // accessory is hidden (and auto-pruned from the selection if it was
  // already added). Example: OS Wheel Kit hides when wheels="metal" on
  // Vision 2 Retrofit, since metal wheels are already Breeze-track.
  hideWhenExistingSystemAnswer?: {
    fieldId: string;
    equalsAnyOf: string[];
  };
}

export interface IncludedWhenRule {
  machineFamilies?: MachineFamily[];
  upgradeIds?: string[];
  accessoryIds?: string[]; // e.g. Bobbin Winder included when Startup Kit selected
}

export interface PriceOverrideRule {
  // When ANY of these conditions hold, apply the override.
  when: {
    hasMachineOrUpgradeSelected?: boolean;
    // Selected machine's family is one of these (e.g. ["statler"] — the
    // pre-loved Ascend-upgrade bundle price applies on any Statler).
    machineFamilies?: MachineFamily[];
  };
  // Flat target price (e.g. 600000 = $6,000). Takes precedence over
  // discountCents when set — survives a HubSpot base-price change.
  overridePriceCents?: number;
  // Replaces overridePriceCents for any pre-loved-channel unit EXCEPT those
  // whose deep-link category is in `exceptCategories` (case-insensitive).
  // Listing the exceptions — not the matches — means a newly added category
  // lands on the pre-loved price, same as the backend.
  prelovedOverride?: { overridePriceCents: number; exceptCategories: string[] };
  // Subtract this from the base price (used when there's no flat target).
  discountCents?: number;
  // True → show the original price struck-through with the new price next to it.
  displayAsStrikethrough: boolean;
}

export type WarningTrigger =
  | { kind: "table_size_lt"; size: number }
  | { kind: "accessory_selected"; ids: string[] };

export interface Warning {
  when: WarningTrigger;
  message: string;
}

export interface ProductRules {
  displayName?: string;
  description?: string;
  hidden?: boolean;
  /** Override for image filename lookup. Defaults to slugify(originalName).
   *  Useful when the HubSpot name is long/SKU-laden but the saved image is
   *  named simply (e.g. Casters). */
  imageSlug?: string;
  /** Distinct image for the small thumbnail (e.g. a single-panel crop of a wide
   *  poster). Falls back to imageSlug/name when unset. */
  thumbImageSlug?: string;
  /** Vimeo or YouTube URL — when set, the detail card shows this video in
   *  place of the product image. */
  videoUrl?: string;
  /** Autoplay the video (muted) when the card opens, instead of showing a
   *  play button. */
  videoAutoplay?: boolean;
  /** Self-hosted product video: bundled `<slug>.webm` + `<slug>.mp4` (poster =
   *  `<slug>.jpg`) under public/images/products/. Autoplays muted + looped as
   *  B-roll. Preferred over videoUrl (no third-party, no domain restrictions). */
  localVideo?: boolean;
  compat?: CompatRule;
  includedWhen?: IncludedWhenRule;
  exclusiveTo?: {
    upgradeIds: string[]; // only visible if one of these upgrades is selected
  };
  defaultOnMachine?: boolean; // auto-add when ANY new machine is selected
  defaultOnUpgrade?: string[]; // auto-add when one of these upgrade IDs is selected
  blocks?: string[]; // auto-remove these when this is added
  suggestsOnRemoval?: string[]; // when removed, prompt to re-add these
  warnings?: Warning[];
  priceOverride?: PriceOverrideRule;
}

/* ── Rules table ───────────────────────────────────────────────── */

// Shared by the Approach Foot pair (same physical product, two price variants
// for new-machine vs retrofit purchase contexts).
const APPROACH_FEET_DESCRIPTION =
  "<p>Feet change in just a few seconds, so you can always have the right foot on.</p>" +
  "<ul>" +
  "<li><strong>Ruler Foot</strong> — perfect ¼\" spacing on all sides, high edges to keep the ruler in place.</li>" +
  "<li><strong>Spoon Foot</strong> — the Hummer of feet; stitches over the worst piecing with ease.</li>" +
  "<li><strong>Couching Foot</strong> — apply baby yarn as you quilt.</li>" +
  "<li><strong>Trapunto Foot</strong> — almost like no foot at all; gets your needle right up into tight spaces.</li>" +
  "<li><strong>Open Toe Foot</strong> — awesome visibility, perfect for ditching.</li>" +
  "</ul>";

// Shared by the Zippered Leaders pair (same product, two purchase contexts).
const ZIPPERED_LEADERS_DESCRIPTION =
  "<p>Easily remove a quilt for any reason and replace it back onto the frame " +
  "in the same exact condition and placement it was before.</p>" +
  "<ul>" +
  "<li>Perfect for unpicking in comfort.</li>" +
  "<li>Perfect for smoothing wrinkled layers.</li>" +
  "<li>Perfect for an “emergency” quilt.</li>" +
  "<li>Perfect for when you need to press “pause” on a quilt for any reason.</li>" +
  "</ul>";

export const RULES: Record<string, ProductRules> = {
  /* ── Hidden duplicates ─────────────────────────────────────── */
  [ID.CASTERS_OLD]: { hidden: true },
  [ID.OLD_APPROACH_FOOT_SYSTEM]: { hidden: true },

  /* ── Upgrades (display names + descriptions) ────────────────── */
  [ID.ASCEND_RETROFIT]: {
    description: "Turn your existing Gammill into a Statler Ascend.",
  },
  [ID.STATLER_RETRO]: {
    displayName: "Statler Retrofit",
    description: "Turn your existing Gammill into a Statler.",
  },
  [ID.VISION_2_RETROFIT]: {
    description: "Turn your existing Gammill into a Vision 2.0.",
  },
  [ID.ASCEND_UPGRADE]: {
    description:
      "Turn your existing Statler into a Statler Ascend " +
      "(specific machine configurations only).",
    // In pre-loved mode this product is surfaced as an optional ACCESSORY
    // (the store reclassifies it — the Machine/Upgrade tab is hidden for
    // pre-loved), letting a pre-loved Statler buyer add Ascend the same way
    // new-Statler buyers get the "+$6,000 Upgrade to Statler Ascend" toggle.
    // Gated to Statler family; priced at a flat $6,000 (vs the $8,499 base)
    // on Show / Classroom / Paint Flaw Statlers and $7,000 on Pre-Loved
    // (dated or graded) Statlers, shown struck-through. Must match views.py
    // _ascend_on_statler_cents().
    compat: { machineFamilies: ["statler"] },
    priceOverride: {
      when: { machineFamilies: ["statler"] },
      overridePriceCents: 600000,
      prelovedOverride: {
        overridePriceCents: 700000,
        exceptCategories: ["Show", "Classroom", "Paint Flaw"],
      },
      displayAsStrikethrough: true,
    },
  },

  /* ── Accessories: simple universals ─────────────────────────── */
  [ID.HYDRAULICS]: {
    description:
      "<ul>" +
      "<li>Pushbutton control to raise or lower the entire Gammill system.</li>" +
      "<li>Perfect for those suffering injuries or pain — it lets you shift position easily.</li>" +
      "<li>Allows you to quilt more comfortably for longer.</li>" +
      "<li>Easier for sharing/renting to a person of different height.</li>" +
      "<li>Includes a set of 4 casters.</li>" +
      "</ul>",
    compat: { purchaseContexts: ["new_machine", "upgrade"] },
    blocks: [ID.CASTERS],
    suggestsOnRemoval: [ID.CASTERS],
  },
  [ID.CASTERS]: {
    // HubSpot SKU name is verbose; surface as "Casters" everywhere.
    displayName: "Casters",
    description:
      "<p>Easily relocate your machine inside the room — on carpet or hard floors.</p>" +
      "<ul>" +
      "<li>Leveling adjustment on each caster.</li>" +
      "<li>2 locking casters for stability, 2 non-locking.</li>" +
      "</ul>",
    imageSlug: "casters",
    localVideo: true,
    compat: { purchaseContexts: ["new_machine", "upgrade"] },
    defaultOnMachine: true,
    defaultOnUpgrade: [
      ID.ASCEND_RETROFIT,
      ID.STATLER_RETRO,
      ID.VISION_2_RETROFIT,
      ID.ASCEND_UPGRADE,
    ],
    // Hydraulics includes casters — when Hydraulics is selected, Casters
    // shows as "Included" and locked. (The Hydraulics `blocks` rule also
    // auto-removes Casters from selection.)
    includedWhen: { accessoryIds: [ID.HYDRAULICS] },
  },
  [ID.LIGHT_BAR]: {
    description:
      "<p><strong>Perfect Lighting without shadows.</strong></p>" +
      "<ul>" +
      "<li>Always perfectly adjusted/aligned over the quilt.</li>" +
      "<li>True Spectrum LED lights run cool.</li>" +
      "<li>Glare-Free lighting allows your pupil to expand, delivering ultimate clarity of vision.</li>" +
      "</ul>",
    localVideo: true,
    compat: { purchaseContexts: ["new_machine", "upgrade"] },
    defaultOnMachine: true,
    defaultOnUpgrade: [
      ID.ASCEND_RETROFIT,
      ID.STATLER_RETRO,
      ID.VISION_2_RETROFIT,
      ID.ASCEND_UPGRADE,
    ],
  },
  [ID.QUILT_CLIPS]: {
    description:
      "<p>Gammill Quilt Clips provide an easy and fast way to control and adjust " +
      "the layers of fabric on your quilting frame. The quilt clips slide over " +
      "the fabric and frame rail, keeping the quilt layers together. They expand " +
      "to adjust to thickly rolled fabric. The clips are perfect for:</p>" +
      "<ul>" +
      "<li>Making quick adjustments to keep your fabric straight.</li>" +
      "<li>Maintaining tension for quilting, even when unpinning the end of your quilt.</li>" +
      "<li>Floating fabric layers.</li>" +
      "</ul>" +
      "<p>A package includes four quilt clips, each of which are 24 inches long.</p>",
    localVideo: true,
    compat: {},
  },
  [ID.RULER_BASE]: {
    description:
      "<p>If you want to do ruler work, this ruler base plate is essential. The " +
      "plate snaps on and off the machine in a second, and strong spring action " +
      "assures that the base stays on securely.</p>" +
      "<p>The Ruler Base Plate provides stability for your rulers while quilting. " +
      "Aluminum and steel construction means you can also use it for pressing " +
      "(yes, with an iron) while the quilt is on the frame.</p>",
    videoUrl: "https://youtu.be/BfLDGfEkl-8",
    videoAutoplay: true,
    imageSlug: "extended-throat-plate",
    compat: {},
  },
  [ID.MICRO_FILL]: {
    description:
      "<p>Used for micro-stippling, pebbling, thread painting, and other " +
      "techniques that require rapid, constant change of motion combined with " +
      "high accuracy.</p>" +
      "<ul><li>Folds up out of the way when not in use.</li></ul>",
    imageSlug: "stippling",
    compat: {},
  },
  [ID.CUTTING_MAT]: {
    description:
      "<p><strong>Self Healing Surface — durable!</strong></p>" +
      "<ul>" +
      "<li>Batting Cuts</li>" +
      "<li>Backing Cuts</li>" +
      "<li>Squaring / Trimming Quilt afterwards</li>" +
      "</ul>" +
      "<p>The mat is marked 20\" x 140\" with approximately an inch all the way " +
      "around, for an approximate total size of 22\" x 142\".</p>",
    imageSlug: "cutting-mats",
    localVideo: true,
    compat: {},
  },

  /* ── Vivid Light (renamed + Included-on-Ascend) ─────────────── */
  [ID.VIVID_LIGHT]: {
    displayName: "Vivid Throat/Arm Light",
    description:
      "<p>This optional light — included with Ascend — expands the shadow-free " +
      "lighted area to the entire quilt, from belly-bar to pickup roller.</p>" +
      "<p>Toggle to black light at the touch of a button to reveal UV markings " +
      "and make white thread on white fabric easy to see.</p>",
    // Thumbnail shows just the "Vivid Lighting" panel of the comparison poster;
    // the detail card shows the full ordinary-vs-vivid image.
    thumbImageSlug: "vivid-light-thumb",
    compat: { machineFamilies: ["statler", "vision"] },
    includedWhen: {
      machineFamilies: ["ascend"],
      upgradeIds: [ID.ASCEND_RETROFIT, ID.ASCEND_UPGRADE],
    },
    defaultOnMachine: true,
    defaultOnUpgrade: [ID.STATLER_RETRO, ID.VISION_2_RETROFIT],
  },

  /* ── Bobbin Winder + Startup Kit bundle ─────────────────────── */
  [ID.BOBBIN_WINDER]: {
    description:
      "<p>Perfectly wound bobbins are required for perfect tension on your quilts.</p>" +
      "<ul>" +
      "<li>Auto Shut-off</li>" +
      "<li>Adjustable speed</li>" +
      "<li>Zero Maintenance</li>" +
      "<li>Quiet</li>" +
      "</ul>",
    imageSlug: "bobbin-winder",
    localVideo: true,
    compat: {},
    defaultOnMachine: true,
    defaultOnUpgrade: [
      ID.ASCEND_RETROFIT,
      ID.STATLER_RETRO,
      ID.VISION_2_RETROFIT,
      ID.ASCEND_UPGRADE,
    ],
    includedWhen: { accessoryIds: [ID.STARTUP_KIT] },
  },
  [ID.STARTUP_KIT]: {
    description:
      "<p>Includes:</p>" +
      "<ul>" +
      "<li>Professional Bobbin Winder</li>" +
      "<li>Maintenance kit</li>" +
      "<li>10 Gammill bobbins</li>" +
      "<li>Quart of oil</li>" +
      "<li>Magnetic pin dish</li>" +
      "<li>4 packs of needles</li>" +
      "<li>7 spools Omni thread</li>" +
      "<li>3 spools Omni V thread</li>" +
      "<li>1 roll of 80/20 batting</li>" +
      "</ul>",
    compat: {},
  },

  /* ── Panto Handles (Vision-only + warnings) ─────────────────── */
  [ID.PANTO_HANDLES]: {
    description:
      "<p>If you're tired of the strain on your back, neck, and shoulders, it's " +
      "time to invest in these handles. Experience the remarkable difference " +
      "they make in your quilting comfort.</p>" +
      "<p>The Panto-Drive Handles fit the Vision 2.0 machine — the one with A/B " +
      "and C/D handle switches.</p>",
    compat: {
      // OR semantics: a Vision new-machine pick OR a Vision 2 Retrofit
      // upgrade both qualify.
      machineFamilies: ["vision"],
      upgradeIds: [ID.VISION_2_RETROFIT],
    },
    warnings: [
      {
        when: { kind: "table_size_lt", size: 12 },
        message:
          "Panto handles take up some space and are mainly suggested for " +
          "use on 14' or 12' tables.",
      },
      {
        when: { kind: "accessory_selected", ids: [ID.CUTTING_MAT] },
        message:
          "The cutting mat will get in the way of doing pantographs on a " +
          "Vision 2.",
      },
    ],
  },

  /* ── Approach Foot pair (context-gated) ─────────────────────── */
  [ID.APPROACH_FOOT_NEW_MACHINE]: {
    displayName: "Approach Foot System",
    description: APPROACH_FEET_DESCRIPTION,
    videoUrl: "https://vimeo.com/680179459",
    imageSlug: "approach-foot-system",
    compat: { purchaseContexts: ["new_machine"] },
  },
  [ID.APPROACH_FEET_RETROFIT]: {
    displayName: "Approach Foot System",
    description: APPROACH_FEET_DESCRIPTION,
    videoUrl: "https://vimeo.com/680179459",
    imageSlug: "approach-foot-system",
    compat: { purchaseContexts: ["upgrade"] },
  },

  /* ── Zippered Leaders pair (context-gated) ──────────────────── */
  [ID.ZIPPERED_LEADERS_NEW_MACHINE]: {
    displayName: "Zippered Leaders",
    description: ZIPPERED_LEADERS_DESCRIPTION,
    imageSlug: "zippered-leaders",
    compat: { purchaseContexts: ["new_machine"] },
  },
  [ID.ZIPPERED_LEADERS_RETROFIT]: {
    displayName: "Zippered Leaders",
    description: ZIPPERED_LEADERS_DESCRIPTION,
    imageSlug: "zippered-leaders",
    compat: { purchaseContexts: ["upgrade"] },
  },

  /* ── Accelerate Membership (strikethrough discount) ─────────── */
  [ID.MEMBERSHIP]: {
    description:
      "<p><strong>Take the Stress out of starting and running your Business!</strong></p>" +
      "<p>Step-By-Step Quilting Business Planning and Training Program</p>" +
      "<ul>" +
      "<li>12 Months of Mentor/Coaching</li>" +
      "<li>Online Pattern Catalog — everything you need to begin or grow your longarm quilting business</li>" +
      "</ul>" +
      "<p>If you're seeking guidance on how to expand your quilting business, tackle challenging " +
      "situations, or locate essential resources, we've got your back! Our team of Accelerate Coaches " +
      "understands the obstacles and frustrations that come with running a quilting business. With our " +
      "coaches and Accelerate lessons, we're here to empower you! You deserve to be a confident and " +
      "prosperous quilting business owner! Gain the motivation and support necessary to take your " +
      "quilting business to the next level.</p>",
    videoUrl: "https://vimeo.com/878772265/2ed4925a7d?fl=pl&fe=cm",
    compat: {},
    priceOverride: {
      when: { hasMachineOrUpgradeSelected: true },
      discountCents: 50000,
      displayAsStrikethrough: true,
    },
  },

  /* ── Tables — all share one generic photo ───────────────────── */
  [ID.HP2_8]:  { imageSlug: "table-size" },
  [ID.HP2_10]: { imageSlug: "table-size" },
  [ID.HP2_11]: { imageSlug: "table-size" },
  [ID.HP2_12]: { imageSlug: "table-size" },
  [ID.GS6_8]:  { imageSlug: "table-size" },
  [ID.GS6_10]: { imageSlug: "table-size" },
  [ID.GS6_11]: { imageSlug: "table-size" },
  [ID.GS6_12]: { imageSlug: "table-size" },
  [ID.GS6_14]: { imageSlug: "table-size" },

  /* ── Vision-2-Retrofit-exclusive ────────────────────────────── */
  [ID.OS_WHEEL_KIT]: {
    description:
      "<p>OS Wheel Kit — a set of 8 wheels specially designed for Original " +
      "Track Machines.</p>",
    compat: {
      upgradeIds: [ID.VISION_2_RETROFIT],
      // Customer with metal wheels already has Breeze-track — no OS Wheel
      // Kit needed. Hide (and auto-prune from selection).
      hideWhenExistingSystemAnswer: {
        fieldId: "wheels",
        equalsAnyOf: ["metal"],
      },
    },
    defaultOnUpgrade: [ID.VISION_2_RETROFIT],
  },

  /* ── Ascend-Upgrade-exclusive accessories ───────────────────── */
  [ID.CPU_MOUNT_ONLY]: {
    compat: { upgradeIds: [ID.ASCEND_UPGRADE] },
    includedWhen: { accessoryIds: [ID.CPU_AND_MONITOR_MOUNT] },
  },
  [ID.MONITOR_MOUNT_ONLY]: {
    compat: { upgradeIds: [ID.ASCEND_UPGRADE] },
    includedWhen: { accessoryIds: [ID.CPU_AND_MONITOR_MOUNT] },
  },
  [ID.CPU_AND_MONITOR_MOUNT]: {
    description:
      "<p>Gammill Computer Mount and Monitor Mount Kit.</p>" +
      "<ul>" +
      "<li><strong>Computer Mount</strong> — flexible bracket configurations to allow mounting in 4 different configurations to suit your system.</li>" +
      "<li><strong>Monitor Mount</strong> — swiveling keyboard/mouse tray, and can be adjusted to suit different heights of people.</li>" +
      "</ul>",
    compat: { upgradeIds: [ID.ASCEND_UPGRADE] },
    // Force-added by the existing-system flow when fluorescent-tube light
    // bar is reported (the rule lives in EXISTING_SYSTEM_FORMS below).
  },
  [ID.DELL_MONITOR]: {
    description:
      "<ul>" +
      "<li><strong>Wide viewing angle:</strong> See your work in superb FHD clarity and get consistent views across a wide 178°/178° viewing angle.</li>" +
      "<li><strong>Easy on the eyes:</strong> Enjoy a flicker-free screen with ComfortView, a software feature that reduces harmful blue light emissions. It's designed to optimize eye comfort even over an extended period of time.</li>" +
      "<li><strong>Ergonomic adjustments:</strong> Tilt your screen and customize monitor height for all-day comfort.</li>" +
      "<li><strong>Integrated audio:</strong> Get acoustic alerts and audio/video capabilities through two built-in speakers.</li>" +
      "<li><strong>Convenient connectivity:</strong> Connect to current and legacy systems via HDMI, VGA and DP ports.</li>" +
      "</ul>",
    compat: { upgradeIds: [ID.ASCEND_UPGRADE] },
  },
  [ID.GAMMILL_COMPUTER_XE5]: {
    compat: { upgradeIds: [ID.ASCEND_UPGRADE] },
    // Force-added when computer OS = Windows 7 or older.
  },
  [ID.STATLER_KEYBOARD]: {
    compat: { upgradeIds: [ID.ASCEND_UPGRADE] },
  },
  [ID.STATLER_MOUSE]: {
    compat: { upgradeIds: [ID.ASCEND_UPGRADE] },
  },
};

/* ── Tables — throat-size gated ────────────────────────────────── */

export interface TableOption {
  id: string;
  type: "HP2" | "GS6";
  length: number; // feet
  isDefault: boolean; // pre-selected; same as priceCents=0 in our data
}

export const TABLES_FOR_THROAT_18: TableOption[] = [
  { id: ID.HP2_12, type: "HP2", length: 12, isDefault: true },
  { id: ID.HP2_10, type: "HP2", length: 10, isDefault: false },
  { id: ID.HP2_8, type: "HP2", length: 8, isDefault: false },
  { id: ID.HP2_11, type: "HP2", length: 11, isDefault: false },
];

export const TABLES_FOR_THROAT_22_PLUS: TableOption[] = [
  { id: ID.GS6_12, type: "GS6", length: 12, isDefault: true },
  { id: ID.GS6_8, type: "GS6", length: 8, isDefault: false },
  { id: ID.GS6_10, type: "GS6", length: 10, isDefault: false },
  { id: ID.GS6_11, type: "GS6", length: 11, isDefault: false },
  { id: ID.GS6_14, type: "GS6", length: 14, isDefault: false },
];

export function tableOptionsForThroatSize(throatSize: number): TableOption[] {
  return throatSize <= 18 ? TABLES_FOR_THROAT_18 : TABLES_FOR_THROAT_22_PLUS;
}

export function isTableId(id: string): boolean {
  const tableIds = new Set<string>([
    ID.HP2_8, ID.HP2_10, ID.HP2_11, ID.HP2_12,
    ID.GS6_8, ID.GS6_10, ID.GS6_11, ID.GS6_12, ID.GS6_14,
  ]);
  return tableIds.has(id);
}

/* ── Existing-system form definitions per upgrade ──────────────── */
//
// Each upgrade has its own form. Fields can trigger:
//   • surcharges    (36" throat = +$1,000 on the Statler Retrofit's price)
//   • discounts     (Statler/Ascend Retrofit trade-in credits: Metal wheels =
//                    −$1,000 Breeze-track; Mod 3-F −$1,000; Mod G −$2,000)
//   • forced adds   (Win 7 or older → force-add Gammill Computer XE5)
//   • warnings      (controller = "Other" → block submit; fluorescent
//                    tubes → force-add CPU+Monitor mount kit)

export type ExistingSystemFieldType = "dropdown" | "radio" | "text";

export interface ExistingSystemFieldOption {
  value: string;
  label: string;
  // Effects fired when this option is the selected value:
  /** Added to the selected upgrade's own price (on screen + the quote line).
   *  Must match views.py RETROFIT_SURCHARGES. */
  surchargeCents?: number;
  /** A retrofit trade-in credit: comes off the order total and lands on the
   *  HubSpot quote as a discount line labeled `discountLabel`. Must match
   *  views.py RETROFIT_TRADE_IN_CREDITS. */
  discountCents?: number;
  discountLabel?: string;
  forceAddProductId?: string;
  blockSubmit?: { message: string };
  warning?: string;
}

export interface ExistingSystemField {
  id: string;
  label: string;
  type: ExistingSystemFieldType;
  required?: boolean;
  options?: ExistingSystemFieldOption[];
  // Visibility: only render when one of these other fields equals one of
  // these values (lets us hide the bulb-type follow-up unless light-bar=yes).
  showWhen?: { fieldId: string; equalsAnyOf: string[] };
}

export interface ExistingSystemForm {
  // Heading and disclaimer shown above the form.
  disclaimer: string;
  fields: ExistingSystemField[];
}

const STANDARD_RETROFIT_DISCLAIMER =
  "We'll contact you after ordering to verify these details via photos. " +
  "Some pricing may adjust based on verification.";

const ASCEND_UPGRADE_DISCLAIMER =
  "Ascend Upgrade will only fit under very specific conditions. Before we " +
  "process your order we'll get photo verification of your system, because " +
  "we don't want to show up to install it and be unable to complete the job.";

const WHEELS_FIELD = (allowBreezeTrackDiscount = true): ExistingSystemField => ({
  id: "wheels",
  label: "Wheel type",
  type: "radio",
  required: true,
  options: [
    {
      value: "metal",
      label: "Metal (Breeze-track)",
      ...(allowBreezeTrackDiscount
        ? {
            // −$1,000 pending verification: no Breeze install + their old
            // crosstrack traded in.
            discountCents: 100000,
            discountLabel: "Breeze-track trade-in credit",
          }
        : {}),
    },
    { value: "plastic", label: "Plastic" },
  ],
});

// Statler / Ascend Retrofit only: the customer's old controller is traded in.
// Optional — skipping it (or "Other") means no credit.
const RETROFIT_CONTROLLER_FIELD: ExistingSystemField = {
  id: "controller",
  label: "Current controller",
  type: "dropdown",
  options: [
    {
      value: "mod_3f",
      label: "Mod 3-F",
      discountCents: 100000,
      discountLabel: "Mod 3-F trade-in credit",
    },
    {
      value: "mod_g",
      label: "Mod G / G2 / G3",
      discountCents: 200000,
      discountLabel: "Mod G trade-in credit",
    },
    { value: "other", label: "Other / none / not sure" },
  ],
};

const MODEL_FIELD: ExistingSystemField = {
  id: "model",
  label: "Current machine model",
  type: "text",
};

const YEAR_FIELD: ExistingSystemField = {
  id: "year",
  label: "Approximate year",
  type: "text",
};

/* ── Engine helpers — read state + rules to derive UI behavior ── */

/** Context the engine reads to answer "should this product show?" etc. */
export interface RulesContext {
  catalog: Catalog;
  selectedMachineId: string | null;
  selectedAccessoryIds: string[];
  selectedTableId: string | null;
  /** Existing-system form answers for the currently-selected upgrade.
   *  Keyed by field id (e.g. "wheels", "controller"). Empty when no
   *  upgrade is selected or no answers given yet. */
  existingSystemAnswers: Record<string, string>;
  /** True when the main selection is a pre-loved machine. Lets preloved-only
   *  products (configurator_preloved_only) surface in this context. */
  preloved?: boolean;
  /** Pre-loved unit's deep-link category ("Pre-Loved" | "Paint Flaw" |
   *  "Show" | "Classroom"); set only in pre-loved mode. Drives
   *  priceOverride.prelovedOverride. */
  prelovedCategory?: string | null;
}

export type PurchaseContextResolved = PurchaseContext | null;

/** Whether the current main selection is a new machine, an upgrade, or
 *  nothing. */
export function purchaseContextOf(ctx: RulesContext): PurchaseContextResolved {
  if (!ctx.selectedMachineId) return null;
  const p = ctx.catalog.byId.get(ctx.selectedMachineId);
  if (!p) return null;
  return p.category === "upgrade" ? "upgrade" : "new_machine";
}

/** Should this accessory show up in the configurator at all?
 *
 *  Rules logic:
 *    1. hidden → false
 *    2. No main selection → false (nothing to gate against)
 *    3. compat.purchaseContexts (if set) gates by new_machine vs upgrade
 *    4. If neither compat.machineFamilies nor compat.upgradeIds is set, the
 *       accessory is universally compatible (returns true here)
 *    5. Otherwise check EITHER (new-machine context + family match) OR
 *       (upgrade context + upgrade-id match). OR-semantics — a product
 *       with both lists shows up in either context.
 */
export function isAccessoryVisible(productId: string, ctx: RulesContext): boolean {
  const rule = RULES[productId];
  if (rule?.hidden) return false;
  if (!ctx.selectedMachineId) return false;

  // A machine is never its own add-on (e.g. the generic Pre-Loved Machine,
  // which the catalog files as an accessory by name).
  if (productId === ctx.selectedMachineId) return false;

  // Pre-loved-only items (configurator_preloved_only) appear only when a
  // pre-loved machine is selected; hidden from the normal new-machine flow.
  const product = ctx.catalog.byId.get(productId);
  if (product?.preloved_only && !ctx.preloved) return false;

  const selected = ctx.catalog.byId.get(ctx.selectedMachineId);
  if (!selected) return false;
  const isNew = selected.category === "machine";
  const isUpgrade = selected.category === "upgrade";
  const ctxKind: PurchaseContext | null = isNew
    ? "new_machine"
    : isUpgrade
      ? "upgrade"
      : null;
  if (!ctxKind) return false;

  if (!rule?.compat) return true;

  if (
    rule.compat.purchaseContexts &&
    !rule.compat.purchaseContexts.includes(ctxKind)
  ) {
    return false;
  }

  // Existing-system answer gate (e.g. wheels=metal hides OS Wheel Kit).
  if (rule.compat.hideWhenExistingSystemAnswer) {
    const { fieldId, equalsAnyOf } = rule.compat.hideWhenExistingSystemAnswer;
    const answer = ctx.existingSystemAnswers[fieldId];
    if (answer && equalsAnyOf.includes(answer)) return false;
  }

  const hasFamilyGate = Boolean(rule.compat.machineFamilies?.length);
  const hasUpgradeGate = Boolean(rule.compat.upgradeIds?.length);
  if (!hasFamilyGate && !hasUpgradeGate) {
    // No specific gating — accessory is universally compatible (subject to
    // the purchaseContexts check above).
    return true;
  }

  if (isNew && hasFamilyGate && selected.category === "machine") {
    if (rule.compat.machineFamilies!.includes(selected.family)) return true;
  }
  if (isUpgrade && hasUpgradeGate) {
    if (rule.compat.upgradeIds!.includes(ctx.selectedMachineId)) return true;
  }

  return false;
}

/** Whether the product should render as "Included" — visible but locked,
 *  contributing $0 to the order. */
export function isProductIncluded(productId: string, ctx: RulesContext): boolean {
  const rule = RULES[productId];
  if (!rule?.includedWhen) return false;
  const selected = ctx.selectedMachineId
    ? ctx.catalog.byId.get(ctx.selectedMachineId) ?? null
    : null;

  if (rule.includedWhen.machineFamilies && selected?.category === "machine") {
    if (rule.includedWhen.machineFamilies.includes(selected.family)) return true;
  }
  if (
    rule.includedWhen.upgradeIds &&
    ctx.selectedMachineId &&
    rule.includedWhen.upgradeIds.includes(ctx.selectedMachineId)
  ) {
    return true;
  }
  if (rule.includedWhen.accessoryIds) {
    for (const aid of rule.includedWhen.accessoryIds) {
      if (ctx.selectedAccessoryIds.includes(aid)) return true;
    }
  }
  return false;
}

/** Accessory IDs that should be pre-selected for a given main selection.
 *  Used when the customer first picks a machine / changes which one. */
export function defaultAccessoriesFor(ctx: RulesContext): string[] {
  // Optional accessories are NOT pre-selected (business decision) — the customer
  // opts into every add-on. Only genuinely INCLUDED items (bundled with the
  // machine/upgrade, shown as "Included") are seeded so they still surface on the
  // quote as $0 lines. The `defaultOnMachine` / `defaultOnUpgrade` flags are
  // intentionally no longer used to pre-check optional add-ons.
  const out: string[] = [];
  if (!purchaseContextOf(ctx)) return out;
  for (const id of Object.keys(RULES)) {
    if (!isAccessoryVisible(id, ctx)) continue;
    if (isProductIncluded(id, ctx)) out.push(id);
  }
  return out;
}

/** Apply mutual-exclusion rules. When `justAdded` is newly in the selection,
 *  drop any accessories the new one blocks. */
export function applyBlocks(
  selectedAccessoryIds: string[],
  justAdded: string,
): string[] {
  const rule = RULES[justAdded];
  if (!rule?.blocks) return selectedAccessoryIds;
  const blocked = new Set(rule.blocks);
  return selectedAccessoryIds.filter((id) => !blocked.has(id));
}

/** Effective per-unit price after discounts/overrides, for use in subtotal
 *  calculations and UI display. */
export function effectivePriceCents(
  productId: string,
  basePriceCents: number,
  ctx: RulesContext,
): number {
  // Items shown as "Included" contribute $0.
  if (isProductIncluded(productId, ctx)) return 0;

  const rule = RULES[productId];
  if (rule?.priceOverride) {
    const { when, discountCents, prelovedOverride } = rule.priceOverride;
    let { overridePriceCents } = rule.priceOverride;
    if (prelovedOverride && ctx.prelovedCategory != null) {
      const category = ctx.prelovedCategory.trim().toLowerCase();
      const excepted = prelovedOverride.exceptCategories.some(
        (c) => c.toLowerCase() === category,
      );
      if (!excepted) overridePriceCents = prelovedOverride.overridePriceCents;
    }
    const selected = ctx.selectedMachineId
      ? ctx.catalog.byId.get(ctx.selectedMachineId)
      : null;
    const familyMatch =
      Boolean(when.machineFamilies?.length) &&
      selected?.category === "machine" &&
      when.machineFamilies!.includes(selected.family);
    const machineMatch =
      Boolean(when.hasMachineOrUpgradeSelected) && Boolean(ctx.selectedMachineId);
    if (familyMatch || machineMatch) {
      if (overridePriceCents != null) return Math.max(0, overridePriceCents);
      return Math.max(0, basePriceCents - (discountCents ?? 0));
    }
  }
  return basePriceCents + existingSystemSurchargeCents(productId, ctx);
}

/** Surcharges the selected upgrade's existing-system answers add to its own
 *  price (36" Statler Retrofit = +$1,000). Must match views.py
 *  RETROFIT_SURCHARGES. */
function existingSystemSurchargeCents(productId: string, ctx: RulesContext): number {
  if (ctx.preloved || productId !== ctx.selectedMachineId) return 0;
  const form = EXISTING_SYSTEM_FORMS[productId];
  if (!form) return 0;
  let cents = 0;
  for (const field of form.fields) {
    const opt = field.options?.find(
      (o) => o.value === ctx.existingSystemAnswers[field.id],
    );
    cents += opt?.surchargeCents ?? 0;
  }
  return cents;
}

/** Returns the original price if a discount/strikethrough is active —
 *  otherwise null. Used by OptionRow to render `$2,189 → $1,689`. */
export function strikethroughOriginalCents(
  productId: string,
  basePriceCents: number,
  ctx: RulesContext,
): number | null {
  const rule = RULES[productId];
  if (!rule?.priceOverride?.displayAsStrikethrough) return null;
  const effective = effectivePriceCents(productId, basePriceCents, ctx);
  return effective !== basePriceCents ? basePriceCents : null;
}

export const EXISTING_SYSTEM_FORMS: Record<string, ExistingSystemForm> = {
  [ID.ASCEND_RETROFIT]: {
    disclaimer: STANDARD_RETROFIT_DISCLAIMER,
    fields: [
      {
        id: "throat",
        label: "Current throat size",
        type: "dropdown",
        required: true,
        options: [
          { value: "22", label: "22\"" },
          { value: "26", label: "26\"" },
          { value: "30", label: "30\"" },
        ],
      },
      MODEL_FIELD,
      WHEELS_FIELD(),
      RETROFIT_CONTROLLER_FIELD,
      YEAR_FIELD,
    ],
  },
  [ID.STATLER_RETRO]: {
    disclaimer: STANDARD_RETROFIT_DISCLAIMER,
    fields: [
      {
        id: "throat",
        label: "Current throat size",
        type: "dropdown",
        required: true,
        options: [
          { value: "22", label: "22\"" },
          { value: "26", label: "26\"" },
          { value: "30", label: "30\"" },
          { value: "36", label: "36\" (+$1,000)", surchargeCents: 100000 },
        ],
      },
      MODEL_FIELD,
      WHEELS_FIELD(),
      RETROFIT_CONTROLLER_FIELD,
      YEAR_FIELD,
    ],
  },
  [ID.VISION_2_RETROFIT]: {
    disclaimer: STANDARD_RETROFIT_DISCLAIMER,
    fields: [
      {
        id: "throat",
        label: "Current throat size",
        type: "dropdown",
        required: true,
        options: [
          { value: "18", label: "18\"" },
          { value: "22", label: "22\"" },
          { value: "26", label: "26\"" },
          { value: "30", label: "30\"" },
        ],
      },
      MODEL_FIELD,
      WHEELS_FIELD(/* no Breeze-track discount for Vision 2 Retrofit */ false),
      YEAR_FIELD,
    ],
  },
  [ID.ASCEND_UPGRADE]: {
    disclaimer: ASCEND_UPGRADE_DISCLAIMER,
    fields: [
      {
        id: "throat",
        label: "Current throat size",
        type: "dropdown",
        required: true,
        options: [
          { value: "22", label: "22\"" },
          { value: "26", label: "26\"" },
          { value: "30", label: "30\"" },
        ],
      },
      {
        id: "controller",
        label: "Current controller model",
        type: "dropdown",
        required: true,
        options: [
          { value: "g", label: "Mod G" },
          { value: "g2", label: "Mod G2" },
          { value: "g3", label: "Mod G3" },
          {
            value: "other",
            label: "Other (not compatible)",
            blockSubmit: {
              message:
                "Ascend Upgrade is only compatible with Mod G, G2, or G3. " +
                "Call us at 417-256-5919 to discuss your options.",
            },
          },
        ],
      },
      {
        ...WHEELS_FIELD(),
        options: [
          { value: "metal", label: "Metal (Breeze-track)" },
          {
            value: "plastic",
            label: "Plastic",
            blockSubmit: {
              message:
                "Your wheels indicate you don't have a Breeze-track system " +
                "and Ascend Upgrade won't fit. You likely need a Statler " +
                "Retrofit instead — switch tabs above.",
            },
          },
        ],
      },
      {
        id: "computer_os",
        label: "Computer operating system",
        type: "dropdown",
        required: true,
        options: [
          { value: "win11", label: "Windows 11" },
          { value: "win10", label: "Windows 10" },
          { value: "win8", label: "Windows 8" },
          {
            value: "win7_or_older",
            label: "Windows 7 or older",
            forceAddProductId: ID.GAMMILL_COMPUTER_XE5,
            warning:
              "Your Windows 7 or older PC isn't compatible. A Gammill " +
              "Computer XE5 has been added to your order.",
          },
        ],
      },
      {
        id: "cs_version",
        label: "CreativeStudio version",
        type: "dropdown",
        required: true,
        options: [
          { value: "8", label: "8" },
          { value: "7.5.4", label: "7.5.4" },
          { value: "7.3", label: "7.3" },
          { value: "7.2", label: "7.2" },
          { value: "7.1", label: "7.1" },
          { value: "7.0", label: "7.0" },
          {
            value: "5",
            label: "5",
            warning:
              "CreativeStudio 5 is older than what Ascend supports. We " +
              "recommend a new computer — consider adding one above.",
          },
          {
            value: "4.2",
            label: "4.2",
            warning:
              "CreativeStudio 4.2 is significantly older than what Ascend " +
              "supports. We recommend a new computer — consider adding one " +
              "above.",
          },
        ],
      },
      {
        id: "has_light_bar",
        label: "Do you have an overhead light bar on the table?",
        type: "radio",
        required: true,
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
      },
      {
        id: "light_bar_type",
        label: "What kind of light bar?",
        type: "radio",
        required: true,
        showWhen: { fieldId: "has_light_bar", equalsAnyOf: ["yes"] },
        options: [
          { value: "bulbs", label: "Individual light bulbs" },
          {
            value: "tubes",
            label: "Fluorescent tubes (shop-light style)",
            forceAddProductId: ID.CPU_AND_MONITOR_MOUNT,
            warning:
              "The Ascend isn't compatible with \"Boersma\" brand light " +
              "bars — the light bar will need to be removed to make space " +
              "for the larger Ascend motors. We've added the larger CPU + " +
              "Monitor Mount Kit you'll need.",
          },
        ],
      },
    ],
  },
};

export interface TradeInCredit {
  label: string;
  cents: number;
}

/** Trade-in credits earned by the selected retrofit's existing-system answers
 *  (Breeze-track wheels, Mod 3-F / Mod G controller). Only the Statler and
 *  Ascend Retrofit forms carry discount options, so new machines, upgrades,
 *  Vision 2 Retrofit and pre-loved units get none. The backend recomputes
 *  these authoritatively — views.py _retrofit_trade_in_discounts(). */
export function retrofitTradeInCredits(
  selectedMachineId: string | null,
  answers: Record<string, string>,
): TradeInCredit[] {
  const form = selectedMachineId ? EXISTING_SYSTEM_FORMS[selectedMachineId] : null;
  if (!form) return [];
  const credits: TradeInCredit[] = [];
  for (const field of form.fields) {
    const opt = field.options?.find((o) => o.value === answers[field.id]);
    if (opt?.discountCents) {
      credits.push({
        label: opt.discountLabel ?? "Trade-in credit",
        cents: opt.discountCents,
      });
    }
  }
  return credits;
}
